import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Clock, MapPin, Phone, Video, Building2, Calendar,
  Check, X, RefreshCw, Loader2, MessageSquare, CheckCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { toManilaDate, getManilaTodayStr } from '../lib/dateUtils';

const InterviewCard = ({
  interview,
  isEmployer = false,
  onUpdate,
  compact = false
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseAction, setResponseAction] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [employerNotes, setEmployerNotes] = useState('');

  // For employer proposing new time
  const [showProposeTimeModal, setShowProposeTimeModal] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  // Parse scheduled time in Manila timezone (UTC+8)
  const scheduledDate = toManilaDate(interview.scheduled_at);
  const formattedDate = format(scheduledDate, 'MMMM d, yyyy');
  const formattedTime = format(scheduledDate, 'h:mm a');
  const dayOfWeek = format(scheduledDate, 'EEEE');

  // Get today's date in Manila timezone as minimum for new proposals
  const minDate = getManilaTodayStr();

  // Status badge styles
  const statusStyles = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    confirmed: 'bg-green-100 text-green-800 border-green-200',
    declined: 'bg-red-100 text-red-800 border-red-200',
    cancelled: 'bg-gray-100 text-gray-800 border-gray-200',
    completed: 'bg-blue-100 text-blue-800 border-blue-200',
    rescheduled: 'bg-orange-100 text-orange-800 border-orange-200'
  };

  const statusLabels = {
    pending: t('interview.pending'),
    confirmed: t('interview.confirmed'),
    declined: t('interview.declined'),
    cancelled: t('interview.cancelled'),
    completed: t('interview.completed'),
    rescheduled: 'Reschedule Requested'
  };

  const interviewTypeIcons = {
    in_person: <Building2 size={16} className="text-blue-600" />,
    phone: <Phone size={16} className="text-green-600" />,
    video: <Video size={16} className="text-purple-600" />
  };

  const interviewTypeLabels = {
    in_person: t('interview.inPerson'),
    phone: t('interview.phone'),
    video: t('interview.video')
  };

  // Applicant responds to interview (accept/decline/reschedule)
  const handleRespond = async () => {
    if (!responseAction) return;

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      const response = await api.post(`/api/interviews/${interview.id}/respond`, {
        action: responseAction,
        message: responseMessage || null
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        const actionLabel = responseAction === 'accept' ? 'accepted' : responseAction === 'decline' ? 'declined' : 'requested reschedule';
        toast.success(`Interview ${actionLabel}!`);
        setShowResponseModal(false);
        setResponseMessage('');
        setResponseAction(null);
        onUpdate?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    } finally {
      setLoading(false);
    }
  };

  // Employer cancels interview
  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this interview?')) return;

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.post(`/api/interviews/${interview.id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        toast.success('Interview cancelled');
        onUpdate?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  };

  // Employer marks interview as complete
  const handleComplete = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.post(`/api/interviews/${interview.id}/complete`, {
        employer_notes: employerNotes || null
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        toast.success('Interview marked as completed');
        setShowCompleteModal(false);
        setEmployerNotes('');
        onUpdate?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete');
    } finally {
      setLoading(false);
    }
  };

  // Employer proposes new time (responds to reschedule request)
  const handleProposeNewTime = async () => {
    if (!newDate || !newTime) {
      toast.error('Please select date and time');
      return;
    }

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('Please log in');
        return;
      }

      const scheduledAt = new Date(`${newDate}T${newTime}:00+08:00`).toISOString();

      const response = await api.put(`/api/interviews/${interview.id}`, {
        scheduled_at: scheduledAt
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        toast.success('New time proposed! Waiting for applicant to confirm.');
        setShowProposeTimeModal(false);
        setNewDate('');
        setNewTime('');
        onUpdate?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to propose new time');
    } finally {
      setLoading(false);
    }
  };

  // Employer declines reschedule request (keeps original time)
  const handleDeclineReschedule = async () => {
    if (!confirm('Decline the reschedule request and keep the original time?')) return;

    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const response = await api.put(`/api/interviews/${interview.id}`, {
        reject_reschedule: true
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      if (response.data.success) {
        toast.success('Reschedule request declined. Original time kept.');
        onUpdate?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline reschedule');
    } finally {
      setLoading(false);
    }
  };

  const openResponseModal = (action) => {
    setResponseAction(action);
    setShowResponseModal(true);
  };

  // Compact view for dashboard lists
  if (compact) {
    return (
      <div className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
        <div className="text-center bg-primary text-white p-2 rounded-lg min-w-[50px]">
          <div className="text-lg font-bold">{format(scheduledDate, 'd')}</div>
          <div className="text-xs uppercase">{format(scheduledDate, 'MMM')}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">
            {isEmployer ? interview.applicant?.name : interview.employer?.business_name || interview.employer?.name}
          </p>
          <p className="text-sm text-muted-foreground truncate">{interview.job?.title}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
            <Clock size={12} />
            {formattedTime}
            <span>•</span>
            {interviewTypeIcons[interview.interview_type]}
            {interviewTypeLabels[interview.interview_type]}
          </div>
        </div>
        <Badge className={statusStyles[interview.status]}>
          {statusLabels[interview.status]}
        </Badge>
      </div>
    );
  }

  return (
    <>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* Header with date */}
          <div className={`p-4 flex items-center gap-4 ${
            interview.status === 'rescheduled' ? 'bg-orange-500 text-white' : 'bg-primary text-white'
          }`}>
            <div className="text-center">
              <div className="text-3xl font-bold">{format(scheduledDate, 'd')}</div>
              <div className="text-sm uppercase">{format(scheduledDate, 'MMM')}</div>
            </div>
            <div className="flex-1">
              <p className="font-semibold">{dayOfWeek}</p>
              <p className="text-sm opacity-90">{formattedTime}</p>
            </div>
            <Badge className={`${statusStyles[interview.status]} border`}>
              {statusLabels[interview.status]}
            </Badge>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Job & Person */}
            <div>
              <h3 className="font-semibold text-lg">{interview.job?.title}</h3>
              <p className="text-muted-foreground">
                {isEmployer
                  ? `with ${interview.applicant?.name}`
                  : `at ${interview.employer?.business_name || interview.employer?.name}`
                }
              </p>
            </div>

            {/* Interview Details */}
            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-2">
                {interviewTypeIcons[interview.interview_type]}
                <span className="font-medium">{interviewTypeLabels[interview.interview_type]}</span>
                <span className="text-muted-foreground">• {interview.duration_minutes} min</span>
              </div>

              {interview.location && (
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                  {/https?:\/\/\S+/i.test(interview.location) ? (
                    <a
                      href={interview.location.match(/https?:\/\/\S+/i)?.[0]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline break-all"
                    >
                      {interview.location}
                    </a>
                  ) : (
                    <span>{interview.location}</span>
                  )}
                </div>
              )}

              {interview.meeting_link && (
                <div className="flex items-center gap-2">
                  <Video size={16} className="text-purple-600" />
                  <a
                    href={interview.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline truncate"
                  >
                    {interview.meeting_link}
                  </a>
                </div>
              )}

              {interview.phone_number && (
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-green-600" />
                  <a href={`tel:${interview.phone_number}`} className="text-primary hover:underline">
                    {interview.phone_number}
                  </a>
                </div>
              )}
            </div>

            {/* Notes */}
            {interview.notes && (
              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p className="text-muted-foreground">{interview.notes}</p>
              </div>
            )}

            {/* Applicant Response (reschedule message) */}
            {interview.applicant_response && (
              <div className={`p-3 rounded-lg text-sm ${
                interview.status === 'rescheduled' ? 'bg-orange-50 border border-orange-200' : 'bg-blue-50'
              }`}>
                <p className={`font-medium mb-1 ${
                  interview.status === 'rescheduled' ? 'text-orange-800' : 'text-blue-800'
                }`}>
                  <MessageSquare size={14} className="inline mr-1" />
                  {interview.status === 'rescheduled' ? 'Reschedule request from' : 'Message from'} {interview.applicant?.name}:
                </p>
                <p className={interview.status === 'rescheduled' ? 'text-orange-700' : 'text-blue-700'}>
                  "{interview.applicant_response}"
                </p>
              </div>
            )}

            {/* Employer Notes (for completed interviews) */}
            {isEmployer && interview.employer_notes && (
              <div className="p-3 rounded-lg text-sm bg-blue-50 border border-blue-200">
                <p className="font-medium mb-1 text-blue-800">
                  <MessageSquare size={14} className="inline mr-1" />
                  Your Interview Notes:
                </p>
                <p className="text-blue-700 whitespace-pre-wrap">{interview.employer_notes}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {/* ===== APPLICANT ACTIONS ===== */}
              {!isEmployer && interview.status === 'pending' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => openResponseModal('accept')}
                    disabled={loading}
                  >
                    <Check size={16} className="mr-1" />
                    Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openResponseModal('reschedule')}
                    disabled={loading}
                  >
                    <RefreshCw size={16} className="mr-1" />
                    Request Reschedule
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => openResponseModal('decline')}
                    disabled={loading}
                  >
                    <X size={16} className="mr-1" />
                    Decline
                  </Button>
                </>
              )}

              {/* ===== APPLICANT ACTIONS - CONFIRMED ===== */}
              {!isEmployer && interview.status === 'confirmed' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  <X size={16} className="mr-1" />
                  Cancel Interview
                </Button>
              )}

              {/* ===== EMPLOYER ACTIONS - RESCHEDULED ===== */}
              {isEmployer && interview.status === 'rescheduled' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => setShowProposeTimeModal(true)}
                    disabled={loading}
                  >
                    <Calendar size={16} className="mr-1" />
                    Propose New Time
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDeclineReschedule}
                    disabled={loading}
                  >
                    <Check size={16} className="mr-1" />
                    Keep Original Time
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <X size={16} className="mr-1" />
                    Cancel Interview
                  </Button>
                </>
              )}

              {/* ===== EMPLOYER ACTIONS - PENDING ===== */}
              {isEmployer && interview.status === 'pending' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowProposeTimeModal(true)}
                    disabled={loading}
                  >
                    <RefreshCw size={16} className="mr-1" />
                    Change Time
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <X size={16} className="mr-1" />
                    Cancel
                  </Button>
                </>
              )}

              {/* ===== EMPLOYER ACTIONS - CONFIRMED ===== */}
              {isEmployer && interview.status === 'confirmed' && (
                <>
                  <Button
                    size="sm"
                    onClick={() => setShowCompleteModal(true)}
                    disabled={loading}
                  >
                    <CheckCircle size={16} className="mr-1" />
                    Mark Completed
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowProposeTimeModal(true)}
                    disabled={loading}
                  >
                    <RefreshCw size={16} className="mr-1" />
                    Reschedule
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <X size={16} className="mr-1" />
                    Cancel
                  </Button>
                </>
              )}

              {/* Loading indicator */}
              {loading && <Loader2 size={16} className="animate-spin ml-2" />}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Response Modal (for applicants) */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {responseAction === 'accept' && 'Accept Interview'}
              {responseAction === 'decline' && 'Decline Interview'}
              {responseAction === 'reschedule' && 'Request Reschedule'}
            </DialogTitle>
            <DialogDescription>
              {responseAction === 'accept' && 'Confirm that you will attend this interview.'}
              {responseAction === 'decline' && 'Let the employer know why you cannot attend.'}
              {responseAction === 'reschedule' && 'Suggest alternative times that work for you.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <p className="font-medium">{interview.job?.title}</p>
              <p className="text-muted-foreground">{formattedDate} at {formattedTime}</p>
            </div>

            {(responseAction === 'decline' || responseAction === 'reschedule') && (
              <div className="space-y-2">
                <Label htmlFor="message">
                  {responseAction === 'decline' ? 'Reason (optional)' : 'Your availability *'}
                </Label>
                <Textarea
                  id="message"
                  placeholder={
                    responseAction === 'decline'
                      ? 'e.g., I have a schedule conflict...'
                      : 'e.g., I am available Monday-Wednesday 2-5 PM, or Friday morning...'
                  }
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  rows={3}
                  required={responseAction === 'reschedule'}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleRespond}
              disabled={loading || (responseAction === 'reschedule' && !responseMessage.trim())}
              variant={responseAction === 'decline' ? 'destructive' : 'default'}
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {responseAction === 'accept' && 'Confirm Attendance'}
              {responseAction === 'decline' && 'Decline Interview'}
              {responseAction === 'reschedule' && 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Modal (for employers) */}
      <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Interview as Completed</DialogTitle>
            <DialogDescription>
              Add any private notes about this interview (only visible to you).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <p className="font-medium">{interview.applicant?.name}</p>
              <p className="text-muted-foreground">for {interview.job?.title}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Interview Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="e.g., Good communication skills, 3 years experience..."
                value={employerNotes}
                onChange={(e) => setEmployerNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleComplete} disabled={loading}>
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Mark Completed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Propose New Time Modal (for employers) */}
      <Dialog open={showProposeTimeModal} onOpenChange={setShowProposeTimeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {interview.status === 'rescheduled' ? 'Propose New Time' : 'Change Interview Time'}
            </DialogTitle>
            <DialogDescription>
              {interview.status === 'rescheduled'
                ? 'Select a new time based on the applicant\'s availability. They will be notified to confirm.'
                : 'Select a new date and time. The applicant will be notified to confirm.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {interview.status === 'rescheduled' && interview.applicant_response && (
              <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg text-sm">
                <p className="font-medium text-orange-800 mb-1">Applicant's availability:</p>
                <p className="text-orange-700">"{interview.applicant_response}"</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-date">Date</Label>
                <Input
                  id="new-date"
                  type="date"
                  min={minDate}
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-time">Time</Label>
                <Input
                  id="new-time"
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProposeTimeModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleProposeNewTime} disabled={loading || !newDate || !newTime}>
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {interview.status === 'rescheduled' ? 'Send New Time' : 'Update Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InterviewCard;
